import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, DownloadSimple, Printer, EnvelopeSimple, Prohibit, MagnifyingGlass, CaretLeft, CaretRight, Trash, CheckSquare, LockSimple } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const PAGE_SIZE = 20;
const statusLabel = { unused: "Frei", active: "Aktiv", used: "Verwendet", disabled: "Deaktiviert" };
const statusColor = { unused: "#22C55E", active: "#F59E0B", used: "#6B7280", disabled: "#EF4444" };
const FILTERS = [["all", "Alle"], ["unused", "Frei"], ["active", "Aktiv"], ["used", "Verwendet"], ["disabled", "Deaktiviert"]];
const PRINT_FILTERS = [["all", "Alle"], ["printed", "Gedruckt"], ["unprinted", "Nicht gedruckt"]];
const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };

export default function AdminCodes() {
  const [required, setRequired] = useState(false);
  const [maxUses, setMaxUses] = useState(1);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ total: 0, page: 1, codes: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [printed, setPrinted] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sendFor, setSendFor] = useState(null);
  const [email, setEmail] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // {mode:'multi'|'free', count}

  const loadSettings = useCallback(() => {
    api.get("/admin/activation/settings").then((r) => setRequired(r.data.required)).catch(() => {});
  }, []);
  const loadCodes = useCallback(() => {
    setLoading(true);
    api.get("/admin/activation/codes/page", { params: { page, page_size: PAGE_SIZE, q, status, printed } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, q, status, printed]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { const id = setTimeout(loadCodes, 250); return () => clearTimeout(id); }, [loadCodes]);
  useEffect(() => { setPage(1); }, [q, status, printed]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const toggle = async (v) => { setRequired(v); await api.put("/admin/activation/settings", { required: v }); toast.success("Gespeichert"); };
  const generate = async (count) => {
    setBusy(true);
    try { await api.post("/admin/activation/codes/generate", { count, max_uses: Math.max(0, parseInt(maxUses) || 0) }); toast.success("Codes generiert"); setPage(1); loadCodes(); }
    finally { setBusy(false); }
  };
  const disable = async (id) => { await api.post(`/admin/activation/codes/${id}/disable`); loadCodes(); };
  const doSend = async () => {
    try { await api.post("/admin/activation/codes/send-email", { code: sendFor.code, email }); toast.success("Code gesendet"); setSendFor(null); setEmail(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const doDelete = async () => {
    try {
      if (confirm.mode === "free") {
        const r = await api.post("/admin/activation/codes/delete-free");
        toast.success(`${r.data.deleted} freie Codes gelöscht`);
      } else {
        const r = await api.post("/admin/activation/codes/delete", { ids: [...selected] });
        toast.success(`${r.data.deleted} gelöscht${r.data.skipped ? `, ${r.data.skipped} geschützt` : ""}`);
      }
      setConfirm(null); exitSelect(); loadCodes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const usesText = (c) => (c.max_uses === 0 ? `${c.uses} eingelöst · unbegrenzt` : `${c.uses} / ${c.max_uses} eingelöst`);

  const exportCsv = async () => {
    const all = (await api.get("/admin/activation/codes")).data;
    const rows = [["Code", "Produkt", "Herkunft", "Status", "Eingeloest", "Max", "Kaeufer", "Datum"]];
    all.forEach((c) => {
      const st = c.state === "redeemed" ? "Verwendet" : c.state === "sent" ? "Verschickt" : "Generiert";
      rows.push([c.code, c.product_name || "", c.source === "shop" ? "Shop" : "Manuell", st, c.uses, c.max_uses === 0 ? "unbegrenzt" : c.max_uses, c.buyer_name || c.buyer_email || "", c.created_at || ""]);
    });
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "safebuddy-codes.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const printCodes = async () => {
    const all = (await api.get("/admin/activation/codes")).data;
    const list = all.filter((c) => c.status !== "used" && c.status !== "disabled");
    const src = list.length ? list : all;
    const labels = src.map((c, i) => `<div class="lbl" style="transform:translateX(${[-6, -4, -2, 0, 2, 4, 6][i % 7]}mm)"><div class="c">${c.code}</div><div class="d">safe-buddy.com</div></div>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>SafeBuddy Etiketten</title>
      <style>@page{size:A4;margin:13.5mm 16.1mm}*{margin:0;padding:0;box-sizing:border-box}html,body{background:#fff}body{font-family:Arial,sans-serif}.sheet{display:flex;flex-wrap:wrap;width:177.8mm}.lbl{width:25.4mm;height:10mm;display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;text-align:center;page-break-inside:avoid}.c{font-size:6.5pt;font-weight:700;letter-spacing:.3pt;line-height:1;white-space:nowrap}.d{font-size:4pt;color:#333;line-height:1;margin-top:.5mm}</style></head>
      <body><div class="sheet">${labels}</div><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
    w.document.close();
    try { await api.post("/admin/activation/codes/mark-printed", { ids: src.map((c) => c.id) }); toast.success(`${src.length} Codes als „Gedruckt" markiert`); loadCodes(); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="admin-codes">
      <div className="sb-card p-5">
        <div className="flex items-center justify-between border-2 border-[var(--app-border)] rounded-2xl px-3 py-2.5 bg-[var(--app-surface)]">
          <span className="font-body text-sm">Aktivierungscode zum Erstellen erforderlich</span>
          <Switch data-testid="activation-toggle" checked={required} onCheckedChange={toggle} />
        </div>

        <div className="mt-4">
          <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Nutzungen pro Code</label>
          <input data-testid="max-uses-input" type="number" min="0" className="sb-input mt-1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </div>

        <div className="flex flex-wrap gap-2 mt-3 items-center">
          <span className="text-xs font-body text-gray-500 uppercase tracking-wider w-full">Codes generieren</span>
          {[1, 10, 25, 50, 100].map((n) => (
            <button key={n} data-testid={`gen-${n}`} disabled={busy} onClick={() => generate(n)}
              className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm font-medium flex items-center gap-1 disabled:opacity-50"><Plus size={14} weight="bold" />{n}</button>
          ))}
          <button data-testid="export-csv" onClick={exportCsv} className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm font-medium flex items-center gap-1"><DownloadSimple size={16} weight="bold" />CSV</button>
          <button data-testid="print-codes" onClick={printCodes} className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm font-medium flex items-center gap-1"><Printer size={16} weight="bold" />Etiketten</button>
        </div>
      </div>

      <div className="sb-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="font-body text-sm text-gray-500"><b>{data.total}</b> Codes {q || status !== "all" || printed !== "all" ? "(gefiltert)" : "gesamt"}</p>
          <div className="flex gap-2">
            <button data-testid="select-mode-toggle" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
              className="sb-btn px-3 py-1.5 bg-[var(--app-surface)] text-xs font-medium flex items-center gap-1"><CheckSquare size={14} weight="bold" />{selectMode ? "Auswahl beenden" : "Auswählen"}</button>
            <button data-testid="delete-free-btn" onClick={() => setConfirm({ mode: "free" })}
              className="sb-btn px-3 py-1.5 text-white text-xs font-medium flex items-center gap-1" style={{ background: "#EF4444" }}><Trash size={14} weight="bold" />Alle Freien löschen</button>
          </div>
        </div>

        {selectMode && (
          <div data-testid="bulk-bar" className="flex items-center justify-between gap-2 mb-3 border-2 border-[var(--app-border)] rounded-2xl px-3 py-2 bg-[var(--app-surface)]">
            <span className="font-body text-sm text-gray-600" data-testid="bulk-count">{selected.size} ausgewählt</span>
            <div className="flex gap-2">
              <button data-testid="bulk-delete-btn" disabled={selected.size === 0} onClick={() => setConfirm({ mode: "multi", count: selected.size })}
                className="sb-btn px-3 py-1.5 text-white text-xs font-medium flex items-center gap-1 disabled:opacity-40" style={{ background: "#EF4444" }}><Trash size={14} weight="bold" />Löschen</button>
              <button data-testid="bulk-cancel" onClick={exitSelect} className="sb-btn px-3 py-1.5 bg-[var(--app-surface)] text-xs font-medium">Abbrechen</button>
            </div>
          </div>
        )}

        <div className="relative mb-2">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="code-search" className="sb-input pl-9" placeholder="Code, Produkt oder Käufer suchen" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="code-filters">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`code-filter-${v}`} onClick={() => setStatus(v)}
              className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${status === v ? "text-white border-transparent" : "bg-[var(--app-surface)] text-gray-500 border-[var(--app-border)]"}`}
              style={status === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="print-filters">
          <span className="text-[11px] font-body text-gray-400 uppercase tracking-wider mr-1">Druckstatus</span>
          {PRINT_FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`print-filter-${v}`} onClick={() => setPrinted(v)}
              className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${printed === v ? "text-white border-transparent" : "bg-[var(--app-surface)] text-gray-500 border-[var(--app-border)]"}`}
              style={printed === v ? { background: "#3B82F6" } : {}}>{l}</button>
          ))}
        </div>

        {loading && <p className="font-body text-sm text-gray-500">…</p>}
        {!loading && data.codes.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="codes-empty">Keine Codes gefunden.</p>}

        <div className="space-y-2" data-testid="codes-list">
          {data.codes.map((c) => (
            <div key={c.id} data-testid={`code-row-${c.code}`} className="border-2 border-[var(--app-border)] rounded-2xl px-3 py-2 bg-[var(--app-surface)]">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {selectMode && (c.status === "unused"
                    ? <input type="checkbox" data-testid={`code-check-${c.code}`} checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} className="h-5 w-5 accent-[var(--app-primary)] mt-0.5 shrink-0" />
                    : <LockSimple size={16} weight="bold" className="text-gray-400 mt-1 shrink-0" data-testid={`code-locked-${c.code}`} />)}
                  <div className="min-w-0">
                    <span className="font-heading font-semibold tracking-wide">{c.code}</span>
                    <p className="text-[11px] font-body text-gray-500">{usesText(c)}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: statusColor[c.status] }}>{statusLabel[c.status]}</span>
                  {c.printed && <span data-testid={`code-printed-${c.code}`} className="text-[10px] font-heading font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: "#3B82F6" }}>Gedruckt · {fmtDate(c.printed_at)}</span>}
                </div>
              </div>
              <p className="text-[11px] font-body text-gray-500 mt-1">
                {c.source === "shop" ? "🛒 Shop" : "✋ Manuell"}{c.product_name ? ` · ${c.product_name}` : ""} · <b>{c.state === "redeemed" ? "Verwendet" : c.state === "sent" ? "Verschickt" : "Generiert"}</b>
              </p>
              <p className="text-[11px] font-body text-gray-400">Erstellt: {fmt(c.created_at)}{c.used_at ? ` · Verwendet: ${fmt(c.used_at)}` : ""}</p>
              {(c.buyer_name || c.buyer_email) && <p className="text-xs font-body text-gray-500 mt-0.5" data-testid={`code-buyer-${c.code}`}>Käufer: {c.buyer_name ? `${c.buyer_name} · ` : ""}{c.buyer_email || "—"}</p>}
              {!selectMode && c.status !== "used" && (
                <div className="flex gap-2 mt-2">
                  <button data-testid={`send-${c.code}`} onClick={() => setSendFor(c)} className="sb-btn px-2.5 py-1.5 bg-[var(--app-surface)] text-xs flex items-center gap-1"><EnvelopeSimple size={14} weight="bold" />Senden</button>
                  <button data-testid={`disable-${c.code}`} onClick={() => disable(c.id)} className="sb-btn px-2.5 py-1.5 bg-[var(--app-surface)] text-xs flex items-center gap-1"><Prohibit size={14} weight="bold" />{c.status === "disabled" ? "Aktivieren" : "Deaktivieren"}</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4" data-testid="codes-pagination">
            <button data-testid="codes-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm flex items-center gap-1 disabled:opacity-40"><CaretLeft size={14} weight="bold" />Zurück</button>
            <span className="font-body text-xs text-gray-500">Seite {data.page} von {totalPages}</span>
            <button data-testid="codes-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="sb-btn px-3 py-2 bg-[var(--app-surface)] text-sm flex items-center gap-1 disabled:opacity-40">Weiter<CaretRight size={14} weight="bold" /></button>
          </div>
        )}
      </div>

      <Dialog open={!!sendFor} onOpenChange={(o) => !o && setSendFor(null)}>
        <DialogContent data-testid="send-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Code per E-Mail senden</DialogTitle>
            <DialogDescription>Aktivierungscode <b>{sendFor?.code}</b> an eine E-Mail-Adresse schicken.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="send-code-email" className="sb-input" type="email" placeholder="kunde@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <button data-testid="send-code-confirm" onClick={doSend} disabled={!email.trim()} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Senden</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Codes löschen?</DialogTitle>
            <DialogDescription>
              {confirm?.mode === "free"
                ? "Alle freien (nie eingelösten) Codes werden dauerhaft gelöscht. Aktive und verwendete Codes bleiben erhalten."
                : `${confirm?.count || 0} ausgewählte Codes werden dauerhaft gelöscht. Nur freie Codes werden entfernt – geschützte Codes bleiben erhalten.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button data-testid="confirm-delete-cancel" onClick={() => setConfirm(null)} className="sb-btn flex-1 py-3 bg-[var(--app-surface)] font-medium">Abbrechen</button>
            <button data-testid="confirm-delete-yes" onClick={doDelete} className="sb-btn flex-1 py-3 text-white font-medium" style={{ background: "#EF4444" }}>Löschen</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
